"use strict";

import Util from "/js/util/util.js";
import Global from "/js/core/global.js";

/**
 * Manages press-and-drag mechanics for a given DOM element.
 */
export default class DragDelegate {

  static _isTouchDevice = undefined;

  #$el;
  #delegator;
  
  #onDownCallback;
  #onMoveCallback;
  #onUpCallback;
  #onClickCallback;

  #isDragging = false;
  #dragWillHonorClick = false;
  #dragStartX = 0;
  #dragStartY = 0;

  /**
   * @param delegator: The thing which will be receiving the callbacks.
   * @param $el: The $DOM element whose mouse/touch events will be listened for.
   */
  constructor(delegator, $el) {
    this.#delegator = delegator;
    this.#$el = $el;

    this.#$el.on("mousedown touchstart", e => this.#onDown(e)); 
  }

  /**
   * Returns `undefined` if no `mousedown` or `touchstart` event has yet been handled by DragDelegate.
   * Otherwise, returns true if client does send touch events. 
   *
   * There's no perfect way of handling this. Am doing it this way: 
   * https://codeburst.io/the-only-way-to-detect-touch-with-javascript-7791a3346685
   */
  static get isTouchDevice() {
    return DragDelegate._isTouchDevice;
  }

  get isStillClick() {
    return this.#dragWillHonorClick;
  }

  setOnDown(f) {
    this.#onDownCallback = f;
  }
  get onDown() {
    return this.#onDownCallback;
  }

  setOnMove(f) {
    this.#onMoveCallback = f;
  }
  get onMove() {
    return this.#onMoveCallback;
  }

  setOnUp(f) {
    this.#onUpCallback = f;
  }
  get onUp() {
    return this.#onUpCallback;
  }

  /**
   * Click-callback gets called only when gesture has not moved more than a certain distance.
   * At the end of a gesture, either the "click" or the "up" callback is called, not both.
   */
  setOnClick(f) {
    this.#onClickCallback = f;
  }
  get onClick() {
    return this.#onClickCallback;
  }

  setCallbacks(down, move, up, click) {
    this.setOnDown(down);
    this.setOnMove(move);
    this.setOnUp(up);
    this.setOnClick(click);
  }

  #onDown(e) {
    if (e.type === "mousedown") {
      // Ignore if not left mouse button
      let isLmb;
      if ("which" in e) { // Typical
        isLmb = (e.which === 1);
      } else if ("button" in e) { // IE, Opera
        isLmb = (e.button === 0);
      } else {
        isLmb = true; // Just to be safe
      }
      if (!isLmb) {
        return;
      }
    }

    if (DragDelegate._isTouchDevice === undefined) {
      DragDelegate._isTouchDevice = (e.type === "touchstart");
      // cl('down - first event. isTouchDevice?', DragDelegate._isTouchDevice);
      // Tell anyone who wants to know that we have decided on whether client is a touch device.
      $(window).trigger("is-touch-device-determined");
    }

    if (DragDelegate._isTouchDevice && e.type === "mousedown") {
      // Prevent touch device from acting on two different down-like events.
      // cl('down - ignoring "mousedown" on touch device.');
      return;
    }
    // cl('down', e.type, this.#delegator);

    const pointX = this.#getMouseTouchX(e);
    const pointY = this.#getMouseTouchY(e);
    this.#isDragging = true;
    this.#dragWillHonorClick = true;
    this.#dragStartX = pointX;
    this.#dragStartY = pointY;
    
    $(window).on("mousemove touchmove", e => this.#onMove(e));
    $(window).on("mouseup touchend touchcancel", e => this.#onUp(e));

    if (this.#onDownCallback) {
      // Call the delegator's callback function with the proper context.
      this.#onDownCallback.call(this.#delegator, pointX, pointY);
    }
  }

  #onMove(e) {
    const x = this.#getMouseTouchX(e);
    const y = this.#getMouseTouchY(e);

    let didCrossThreshold = false;
    if (this.#dragWillHonorClick) {
      const distance = Util.distance(x, y, this.#dragStartX, this.#dragStartY);
      if (distance > 8) {
        // TODO(felarca): Find a way to un-hardcode, and use a 'physical' distance value?
        // cl('move - no longer a click', this.#delegator);
        this.#dragWillHonorClick = false;
        didCrossThreshold = true;
      }
    }

    if (this.#onMoveCallback) {
      const deltaX = x - this.#dragStartX;
      const deltaY = y - this.#dragStartY;
      this.#onMoveCallback.call(this.#delegator, deltaX, deltaY, didCrossThreshold);
    }    
  }

  #onUp(e) {
    this.clearDragHandlers();

    const x = this.#getMouseTouchX(e);
    const y = this.#getMouseTouchY(e);
    const deltaX = x - this.#dragStartX;
    const deltaY = y - this.#dragStartY;

    if (this.#dragWillHonorClick) {
      // cl('up, will treat as click', this.#delegator);
      if (this.#onClickCallback) {
        // TODO: change callback params to always pass "e" as first param, which would require refactoring everywhere else tho
        this.#onClickCallback.call(this.#delegator, x, y, deltaX, deltaY, e);   
      }
    } else {
      // cl('up', this.#delegator);
      if (this.#onUpCallback) {
        this.#onUpCallback.call(this.#delegator, x, y, deltaX, deltaY);
      }
    }
  }

  clearDragHandlers() {
    // TODO(felarca): Wholesale removal of these listeners has the potential to cause conflicts.
    $(window).off("mouseup touchend touchcancel");
    $(window).off("mousemove touchmove");
    this.#isDragging = false;
  }

  // TODO: These different return values might NOT equivalent. Eg, iPad drag-and-drop drags have wrong scale factor (but only on y-axis?!).
  #getMouseTouchX(e) {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientX;
    } else if (e.originalEvent && e.originalEvent.targetTouches && e.originalEvent.targetTouches.length > 0) {
      return e.originalEvent.targetTouches[0].clientX;
    } else if (e.originalEvent && e.originalEvent.changedTouches && e.originalEvent.changedTouches.length > 0) {
      return e.originalEvent.changedTouches[0].clientX;
    } else if (e.screenX) {
      return e.screenX;
    }
    return 0;
  }

  #getMouseTouchY(e) {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientY;
    } else if (e.originalEvent && e.originalEvent.targetTouches && e.originalEvent.targetTouches.length > 0) {
      return e.originalEvent.targetTouches[0].clientY;
    } else if (e.originalEvent && e.originalEvent.changedTouches && e.originalEvent.changedTouches.length > 0) {
      return e.originalEvent.changedTouches[0].clientY;
    } else if (e.screenY) {
      return e.screenY;
    }
    return 0;
  }
}
